import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import {
  InstitutionsAPI, YearsAPI, ClassesAPI, SectionsAPI, SubjectsAPI, TenantsAPI, CampusesAPI,
} from "@/lib/sprint2-api";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, CalendarRange, Layers, Users, BookMarked, Upload } from "lucide-react";
import { Link } from "react-router-dom";

export default function AcademicSetup() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("super_admin");
  const queryClient = useQueryClient();

  // Explicit user selections (empty string = "no explicit pick, use first available").
  const [tenantId, setTenantId] = useState("");
  const [institutionId, setInstitutionId] = useState("");
  const [campusId, setCampusId] = useState("");
  const [yearId, setYearId] = useState("");
  const [classId, setClassId] = useState("");

  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: () => TenantsAPI.list(),
    enabled: isSuper,
  });

  // Derived: explicit pick wins, else first-available, else (non-super) user's own tenant.
  const effectiveTenantId = isSuper
    ? (tenantId || tenantsQuery.data?.[0]?.id || "")
    : (user?.tenant_id || "");
  const tenantParam = isSuper && effectiveTenantId ? { tenant_id: effectiveTenantId } : undefined;

  const institutionsQuery = useQuery({
    queryKey: ["institutions", effectiveTenantId],
    queryFn: () => InstitutionsAPI.list(tenantParam),
    enabled: !!effectiveTenantId,
  });
  const effectiveInstitutionId =
    (institutionsQuery.data?.some((i) => i.id === institutionId) ? institutionId : "") ||
    institutionsQuery.data?.[0]?.id ||
    "";

  // Campuses are scoped to the chosen institution. Campus is OPTIONAL — most setup
  // (years, classes, subjects) lives at the institution level, but campus is shown
  // so admins can mentally anchor where they are.
  const campusesQuery = useQuery({
    queryKey: ["ac-campuses", effectiveInstitutionId, effectiveTenantId],
    queryFn: () => CampusesAPI.list({ institution_id: effectiveInstitutionId, ...(tenantParam || {}) }),
    enabled: !!effectiveInstitutionId,
  });
  const effectiveCampusId =
    (campusesQuery.data?.some((c) => c.id === campusId) ? campusId : "") || "";

  const yearsQuery = useQuery({
    queryKey: ["years", effectiveInstitutionId],
    queryFn: () => YearsAPI.list({ institution_id: effectiveInstitutionId, ...(tenantParam || {}) }),
    enabled: !!effectiveInstitutionId,
  });
  const effectiveYearId = (() => {
    const list = yearsQuery.data || [];
    if (list.some((y) => y.id === yearId)) return yearId;
    const current = list.find((y) => y.is_current);
    return current?.id || list[0]?.id || "";
  })();

  const classesQuery = useQuery({
    queryKey: ["classes", effectiveYearId],
    queryFn: () => ClassesAPI.list({ academic_year_id: effectiveYearId, ...(tenantParam || {}) }),
    enabled: !!effectiveYearId,
  });
  const effectiveClassId =
    (classesQuery.data?.some((c) => c.id === classId) ? classId : "") ||
    classesQuery.data?.[0]?.id ||
    "";

  const sectionsQuery = useQuery({
    queryKey: ["sections", effectiveClassId],
    queryFn: () => SectionsAPI.list({ class_id: effectiveClassId, ...(tenantParam || {}) }),
    enabled: !!effectiveClassId,
  });
  const subjectsQuery = useQuery({
    queryKey: ["subjects", effectiveClassId],
    queryFn: () => SubjectsAPI.list({ class_id: effectiveClassId, ...(tenantParam || {}) }),
    enabled: !!effectiveClassId,
  });

  const invalidate = (keys) => keys.forEach((k) => queryClient.invalidateQueries({ queryKey: k }));

  return (
    <AppShell>
      <div className="p-8" data-testid="academic-setup-page">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
<h1 className="font-display font-extrabold tracking-tight text-4xl">Academic setup</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Compose your academic year tree: <span className="font-mono text-xs px-1 bg-secondary rounded">years → classes → sections → subjects</span>.
            </p>
          </div>
          <Link to="/bulk-import">
            <Button variant="outline" data-testid="goto-bulk-import">
              <Upload className="h-3.5 w-3.5 mr-1.5" /> Bulk import
            </Button>
          </Link>
        </div>

        {/* Scope selectors */}
        <div className="flex flex-wrap gap-3 mb-6 p-4 border border-border rounded-md bg-card">
          {isSuper && (
            <ScopeSelect
              label="Tenant"
              value={effectiveTenantId}
              onChange={(v) => { setTenantId(v); setInstitutionId(""); setYearId(""); setClassId(""); }}
              options={(tenantsQuery.data || []).map((t) => ({ value: t.id, label: t.name }))}
              testid="scope-tenant"
              placeholder="Select tenant"
            />
          )}
          <ScopeSelect
            label="Institution"
            value={effectiveInstitutionId}
            onChange={(v) => { setInstitutionId(v); setCampusId(""); setYearId(""); setClassId(""); }}
            options={(institutionsQuery.data || []).map((i) => ({ value: i.id, label: i.name }))}
            testid="scope-institution"
            placeholder={institutionsQuery.isLoading ? "Loading…" : "Select institution"}
          />
          <ScopeSelect
            label="Campus"
            value={effectiveCampusId || "_all_"}
            onChange={(v) => setCampusId(v === "_all_" ? "" : v)}
            options={[{ value: "_all_", label: "All campuses" }, ...((campusesQuery.data || []).map((c) => ({ value: c.id, label: c.name })))]}
            testid="scope-campus"
            placeholder={effectiveInstitutionId ? (campusesQuery.isLoading ? "Loading…" : "All campuses") : "—"}
            disabled={!effectiveInstitutionId}
          />
          <ScopeSelect
            label="Academic year"
            value={effectiveYearId}
            onChange={(v) => { setYearId(v); setClassId(""); }}
            options={(yearsQuery.data || []).map((y) => ({ value: y.id, label: y.name + (y.is_current ? " · current" : "") }))}
            testid="scope-year"
            placeholder={effectiveInstitutionId ? "Select year" : "—"}
            disabled={!effectiveInstitutionId}
          />
          <ScopeSelect
            label="Class"
            value={effectiveClassId}
            onChange={setClassId}
            options={(classesQuery.data || []).map((c) => ({ value: c.id, label: c.name }))}
            testid="scope-class"
            placeholder={effectiveYearId ? "Select class" : "—"}
            disabled={!effectiveYearId}
          />
        </div>

        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground mt-6 mb-2">Structure</div>
        <Tabs defaultValue="years">
          <TabsList>
            <TabsTrigger value="years" data-testid="tab-years"><CalendarRange className="h-3.5 w-3.5 mr-1.5" />Years</TabsTrigger>
            <TabsTrigger value="terms" data-testid="tab-terms"><CalendarRange className="h-3.5 w-3.5 mr-1.5" />Terms</TabsTrigger>
            <TabsTrigger value="classes" data-testid="tab-classes"><Layers className="h-3.5 w-3.5 mr-1.5" />Classes</TabsTrigger>
            <TabsTrigger value="sections" data-testid="tab-sections"><Users className="h-3.5 w-3.5 mr-1.5" />Sections</TabsTrigger>
            <TabsTrigger value="subjects" data-testid="tab-subjects"><BookMarked className="h-3.5 w-3.5 mr-1.5" />Subjects</TabsTrigger>
          </TabsList>

          {/* Years */}
          <TabsContent value="years" className="mt-6">
            <YearsTab
              institutionId={effectiveInstitutionId}
              years={yearsQuery.data}
              loading={yearsQuery.isLoading}
              onRefresh={() => invalidate([["years", effectiveInstitutionId]])}
            />
          </TabsContent>

          {/* Terms */}
          <TabsContent value="terms" className="mt-6">
            <TermsTab academicYearId={effectiveYearId} tenantId={effectiveTenantId} />
          </TabsContent>

          {/* Classes */}
          <TabsContent value="classes" className="mt-6">
            <ClassesTab
              institutionId={effectiveInstitutionId}
              yearId={effectiveYearId}
              classes={classesQuery.data}
              loading={classesQuery.isLoading}
              onRefresh={() => invalidate([["classes", effectiveYearId]])}
            />
          </TabsContent>

          {/* Sections */}
          <TabsContent value="sections" className="mt-6">
            <SectionsTab
              classId={effectiveClassId}
              sections={sectionsQuery.data}
              loading={sectionsQuery.isLoading}
              onRefresh={() => invalidate([["sections", effectiveClassId]])}
            />
          </TabsContent>

          {/* Subjects */}
          <TabsContent value="subjects" className="mt-6">
            <SubjectsTab
              classId={effectiveClassId}
              subjects={subjectsQuery.data}
              loading={subjectsQuery.isLoading}
              onRefresh={() => invalidate([["subjects", effectiveClassId]])}
            />
          </TabsContent>
        </Tabs>

        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground mt-10 mb-2">Calendar & Rules</div>
        <Tabs defaultValue="periods">
          <TabsList>
            <TabsTrigger value="periods" data-testid="tab-periods"><CalendarRange className="h-3.5 w-3.5 mr-1.5" />Periods</TabsTrigger>
            <TabsTrigger value="holidays" data-testid="tab-holidays"><CalendarRange className="h-3.5 w-3.5 mr-1.5" />Holidays</TabsTrigger>
            <TabsTrigger value="grading" data-testid="tab-grading"><BookMarked className="h-3.5 w-3.5 mr-1.5" />Grading scale</TabsTrigger>
          </TabsList>
          <TabsContent value="periods" className="mt-6"><PeriodsTab institutionId={effectiveInstitutionId} tenantId={effectiveTenantId} /></TabsContent>
          <TabsContent value="holidays" className="mt-6"><HolidaysTab institutionId={effectiveInstitutionId} tenantId={effectiveTenantId} /></TabsContent>
          <TabsContent value="grading" className="mt-6"><GradingScaleTab institutionId={effectiveInstitutionId} tenantId={effectiveTenantId} /></TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

function ScopeSelect({ label, value, onChange, options, placeholder, testid, disabled }) {
  return (
    <div className="space-y-1.5 min-w-[180px]">
      <Label className="text-[10px] font-semibold uppercase tracking-wider">{label}</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled || !options.length}>
        <SelectTrigger className="w-full" data-testid={testid}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// ---------------- Years tab ----------------
function YearsTab({ institutionId, years, loading, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "", is_current: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await YearsAPI.create({ institution_id: institutionId, ...form });
      toast.success("Academic year created");
      setForm({ name: "", start_date: "", end_date: "", is_current: false });
      setOpen(false);
      onRefresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await YearsAPI.remove(id); toast.success("Year removed"); onRefresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <SectionShell
      title="Academic years"
      count={years?.length || 0}
      action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button disabled={!institutionId} data-testid="create-year-button"><Plus className="h-3.5 w-3.5 mr-1.5" />Academic year</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New academic year</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <SF label="Name (e.g. 2025-26)">
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="year-form-name" />
              </SF>
              <div className="grid grid-cols-2 gap-3">
                <SF label="Start"><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="year-form-start" /></SF>
                <SF label="End"><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></SF>
              </div>
              <label className="flex items-center gap-2">
                <Switch checked={form.is_current} onCheckedChange={(v) => setForm({ ...form, is_current: v })} data-testid="year-form-current" />
                <span className="text-sm">Mark as current year</span>
              </label>
              {error && <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">{error}</div>}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={busy} data-testid="year-form-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      {!institutionId ? (
        <Empty msg="Select an institution to manage academic years." />
      ) : loading ? <Loading /> : (years?.length ? (
        <Table>
          <TableHeader><TableRow><TableHead>Year</TableHead><TableHead>Period</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {years.map((y) => (
              <TableRow key={y.id} data-testid={`year-row-${y.id}`}>
                <TableCell className="font-medium">{y.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{y.start_date || "—"} → {y.end_date || "—"}</TableCell>
                <TableCell>
                  {y.is_current
                    ? <Badge className="bg-primary/10 text-primary border-primary/20">CURRENT</Badge>
                    : <Badge variant="outline" className="text-muted-foreground">past / future</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => remove(y.id)} data-testid={`year-delete-${y.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : <Empty msg="No academic years yet for this institution." />)}
    </SectionShell>
  );
}

// ---------------- Classes tab ----------------
function ClassesTab({ institutionId, yearId, classes, loading, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", order: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await ClassesAPI.create({
        institution_id: institutionId,
        academic_year_id: yearId,
        ...form,
        order: Number(form.order) || 0,
      });
      toast.success("Class added");
      setForm({ name: "", code: "", order: 0 });
      setOpen(false);
      onRefresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this class and its sections / subjects?")) return;
    try { await ClassesAPI.remove(id); toast.success("Class removed"); onRefresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <SectionShell
      title="Classes"
      count={classes?.length || 0}
      action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button disabled={!yearId} data-testid="create-class-button"><Plus className="h-3.5 w-3.5 mr-1.5" />Class</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New class</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <SF label="Name (e.g. Grade 5)"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="class-form-name" /></SF>
              <div className="grid grid-cols-2 gap-3">
                <SF label="Code"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="G5" className="font-mono" /></SF>
                <SF label="Display order"><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></SF>
              </div>
              {error && <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">{error}</div>}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={busy} data-testid="class-form-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Add</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      {!yearId ? <Empty msg="Pick a year to manage its classes." /> :
        loading ? <Loading /> :
        classes?.length ? (
          <Table>
            <TableHeader><TableRow><TableHead>Class</TableHead><TableHead>Code</TableHead><TableHead>Order</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {classes.map((c) => (
                <TableRow key={c.id} data-testid={`class-row-${c.id}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="font-mono text-xs">{c.code || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.order}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => remove(c.id)} data-testid={`class-delete-${c.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : <Empty msg="No classes yet. Add Grade 5, Grade 6, FY BSc, etc." />}
    </SectionShell>
  );
}

// ---------------- Sections tab ----------------
function SectionsTab({ classId, sections, loading, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", capacity: 40 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await SectionsAPI.create({ class_id: classId, name: form.name, capacity: Number(form.capacity) || 40 });
      toast.success("Section added");
      setForm({ name: "", capacity: 40 });
      setOpen(false);
      onRefresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await SectionsAPI.remove(id); toast.success("Section removed"); onRefresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <SectionShell
      title="Sections"
      count={sections?.length || 0}
      action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button disabled={!classId} data-testid="create-section-button"><Plus className="h-3.5 w-3.5 mr-1.5" />Section</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New section</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <SF label="Name (e.g. A)"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="section-form-name" /></SF>
                <SF label="Capacity"><Input type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="section-form-capacity" /></SF>
              </div>
              {error && <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">{error}</div>}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={busy} data-testid="section-form-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Add</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      {!classId ? <Empty msg="Pick a class to manage sections." /> :
        loading ? <Loading /> :
        sections?.length ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="sections-grid">
            {sections.map((s) => (
              <div key={s.id} className="border border-border rounded-md p-4 bg-card flex flex-col gap-1 relative group" data-testid={`section-card-${s.id}`}>
                <div className="font-display font-extrabold text-3xl tracking-tight">{s.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Capacity {s.capacity}</div>
                <Button size="icon" variant="ghost" className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition" onClick={() => remove(s.id)} data-testid={`section-delete-${s.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        ) : <Empty msg="No sections yet for this class." />}
    </SectionShell>
  );
}

// ---------------- Subjects tab ----------------
function SubjectsTab({ classId, subjects, loading, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", code: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await SubjectsAPI.create({ class_id: classId, name: form.name, code: form.code || undefined });
      toast.success("Subject added");
      setForm({ name: "", code: "" });
      setOpen(false);
      onRefresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await SubjectsAPI.remove(id); toast.success("Subject removed"); onRefresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <SectionShell
      title="Subjects"
      count={subjects?.length || 0}
      action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button disabled={!classId} data-testid="create-subject-button"><Plus className="h-3.5 w-3.5 mr-1.5" />Subject</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New subject</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <SF label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="subject-form-name" /></SF>
              <SF label="Code"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="font-mono" placeholder="MATH5" /></SF>
              {error && <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">{error}</div>}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={busy} data-testid="subject-form-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Add</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      {!classId ? <Empty msg="Pick a class to manage subjects." /> :
        loading ? <Loading /> :
        subjects?.length ? (
          <Table>
            <TableHeader><TableRow><TableHead>Subject</TableHead><TableHead>Code</TableHead><TableHead>Teachers</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {subjects.map((s) => (
                <TableRow key={s.id} data-testid={`subject-row-${s.id}`}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell className="font-mono text-xs">{s.code || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{s.teacher_ids?.length || 0}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => remove(s.id)} data-testid={`subject-delete-${s.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : <Empty msg="No subjects yet for this class." />}
    </SectionShell>
  );
}

// ---------------- Shared ----------------
function SectionShell({ title, count, action, children }) {
  return (
    <div className="border border-border rounded-md bg-card">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="font-display font-bold">{title}</div>
          <Badge variant="outline" className="text-[10px]">{count}</Badge>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}
function Empty({ msg }) {
  return <div className="p-10 text-center text-sm text-muted-foreground">{msg}</div>;
}
function Loading() {
  return <div className="p-8 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /></div>;
}
function SF({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}

// ============ Terms / Periods / Holidays / Grading ============
import { useState as _useState } from "react";
import { useQuery as _useQuery } from "@tanstack/react-query";
import { TermsAPI, PeriodsAPI, HolidaysAPI, GradingScalesAPI } from "@/lib/academic-extras-api";
import { Trash2 as _Trash2 } from "lucide-react";

function TermsTab({ academicYearId, tenantId }) {
  const q = _useQuery({ queryKey: ["terms", tenantId || "self", academicYearId], queryFn: () => TermsAPI.list(academicYearId, tenantId), enabled: !!academicYearId });
  const [name, setName] = _useState(""); const [start, setStart] = _useState(""); const [end, setEnd] = _useState("");
  if (!academicYearId) return <Empty msg="Pick an academic year to manage terms." />;
  const add = async () => { if (!name || !start || !end) return toast.error("Name + dates required"); try { await TermsAPI.create({ academic_year_id: academicYearId, name, start_date: start, end_date: end, order: (q.data || []).length }, tenantId); setName(""); setStart(""); setEnd(""); q.refetch(); toast.success("Term added"); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };
  const remove = async (id) => { await TermsAPI.remove(id, tenantId); q.refetch(); toast.success("Term removed"); };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-2"><Input placeholder="Term 1" value={name} onChange={(e) => setName(e.target.value)} data-testid="term-name" /><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="term-start" /><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="term-end" /><Button onClick={add} data-testid="term-add"><Plus className="h-4 w-4 mr-1.5" /> Add</Button></div>
      <div className="border border-border rounded-md divide-y divide-border">{(q.data || []).map((t) => (<div key={t.id} className="flex items-center justify-between px-3 py-2"><div><div className="text-sm font-medium">{t.name}</div><div className="text-xs text-muted-foreground">{t.start_date} → {t.end_date}</div></div><Button size="icon" variant="ghost" onClick={() => remove(t.id)} data-testid={`term-del-${t.id}`}><_Trash2 className="h-3.5 w-3.5 text-destructive" /></Button></div>))}{!(q.data || []).length && <Empty msg="No terms yet." />}</div>
    </div>
  );
}

function PeriodsTab({ institutionId, tenantId }) {
  const q = _useQuery({ queryKey: ["periods", tenantId || "self", institutionId], queryFn: () => PeriodsAPI.list(institutionId, tenantId), enabled: !!institutionId });
  const [name, setName] = _useState(""); const [start, setStart] = _useState(""); const [end, setEnd] = _useState("");
  if (!institutionId) return <Empty msg="Pick an institution first." />;
  const add = async () => { if (!name || !start || !end) return toast.error("Name + times required"); try { await PeriodsAPI.create({ institution_id: institutionId, name, start_time: start, end_time: end, order: (q.data || []).length }, tenantId); setName(""); setStart(""); setEnd(""); q.refetch(); toast.success("Period added"); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };
  const remove = async (id) => { await PeriodsAPI.remove(id, tenantId); q.refetch(); };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-2"><Input placeholder="Period 1" value={name} onChange={(e) => setName(e.target.value)} data-testid="period-name" /><Input type="time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="period-start" /><Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="period-end" /><Button onClick={add} data-testid="period-add"><Plus className="h-4 w-4 mr-1.5" /> Add</Button></div>
      <div className="border border-border rounded-md divide-y divide-border">{(q.data || []).map((p) => (<div key={p.id} className="flex items-center justify-between px-3 py-2"><div className="text-sm">{p.name} <span className="font-mono text-xs text-muted-foreground ml-2">{p.start_time} – {p.end_time}</span></div><Button size="icon" variant="ghost" onClick={() => remove(p.id)} data-testid={`period-del-${p.id}`}><_Trash2 className="h-3.5 w-3.5 text-destructive" /></Button></div>))}{!(q.data || []).length && <Empty msg="No periods yet." />}</div>
    </div>
  );
}

function HolidaysTab({ institutionId, tenantId }) {
  const q = _useQuery({ queryKey: ["holidays", tenantId || "self", institutionId], queryFn: () => HolidaysAPI.list(institutionId, tenantId), enabled: !!institutionId });
  const [name, setName] = _useState(""); const [date, setDate] = _useState(""); const [end, setEnd] = _useState("");
  if (!institutionId) return <Empty msg="Pick an institution first." />;
  const add = async () => { if (!name || !date) return toast.error("Name + date required"); try { await HolidaysAPI.create({ institution_id: institutionId, name, date, end_date: end || null }, tenantId); setName(""); setDate(""); setEnd(""); q.refetch(); toast.success("Holiday added"); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };
  const remove = async (id) => { await HolidaysAPI.remove(id, tenantId); q.refetch(); };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-2"><Input placeholder="Diwali" value={name} onChange={(e) => setName(e.target.value)} data-testid="holiday-name" /><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="holiday-date" /><Input type="date" placeholder="End (opt)" value={end} onChange={(e) => setEnd(e.target.value)} /><Button onClick={add} data-testid="holiday-add"><Plus className="h-4 w-4 mr-1.5" /> Add</Button></div>
      <div className="border border-border rounded-md divide-y divide-border">{(q.data || []).map((h) => (<div key={h.id} className="flex items-center justify-between px-3 py-2"><div><div className="text-sm font-medium">{h.name}</div><div className="text-xs text-muted-foreground">{h.date}{h.end_date ? ` → ${h.end_date}` : ""}</div></div><Button size="icon" variant="ghost" onClick={() => remove(h.id)} data-testid={`holiday-del-${h.id}`}><_Trash2 className="h-3.5 w-3.5 text-destructive" /></Button></div>))}{!(q.data || []).length && <Empty msg="No holidays yet." />}</div>
    </div>
  );
}

const DEFAULT_BANDS = [
  { label: "A+", min: 90, max: 100, gpa: 4.0, is_pass: true },
  { label: "A", min: 80, max: 89.99, gpa: 3.5, is_pass: true },
  { label: "B", min: 70, max: 79.99, gpa: 3.0, is_pass: true },
  { label: "C", min: 60, max: 69.99, gpa: 2.5, is_pass: true },
  { label: "D", min: 50, max: 59.99, gpa: 2.0, is_pass: true },
  { label: "F", min: 0, max: 49.99, gpa: 0, is_pass: false },
];

function GradingScaleTab({ institutionId, tenantId }) {
  const q = _useQuery({ queryKey: ["scales", tenantId || "self", institutionId], queryFn: () => GradingScalesAPI.list(institutionId, tenantId), enabled: !!institutionId });
  const [scaleName, setScaleName] = _useState("Standard 6-band");
  if (!institutionId) return <Empty msg="Pick an institution first." />;
  const add = async () => { try { await GradingScalesAPI.create({ institution_id: institutionId, name: scaleName, bands: DEFAULT_BANDS, is_default: (q.data || []).length === 0 }, tenantId); q.refetch(); toast.success("Scale created"); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } };
  const remove = async (id) => { await GradingScalesAPI.remove(id, tenantId); q.refetch(); };
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2"><Input value={scaleName} onChange={(e) => setScaleName(e.target.value)} data-testid="scale-name" placeholder="Scale name" /><Button onClick={add} data-testid="scale-add"><Plus className="h-4 w-4 mr-1.5" /> Create with default 6 bands</Button></div>
      <div className="space-y-2">{(q.data || []).map((s) => (<div key={s.id} className="border border-border rounded-md p-3"><div className="flex items-center justify-between mb-2"><div className="text-sm font-medium">{s.name} {s.is_default && <Badge variant="outline" className="ml-2 text-[10px]">default</Badge>}</div><Button size="icon" variant="ghost" onClick={() => remove(s.id)} data-testid={`scale-del-${s.id}`}><_Trash2 className="h-3.5 w-3.5 text-destructive" /></Button></div><div className="flex flex-wrap gap-1.5">{s.bands.map((b, i) => (<code key={i} className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${b.is_pass ? "bg-secondary" : "bg-destructive/15 text-destructive"}`}>{b.label} {b.min}-{b.max} ({b.gpa})</code>))}</div></div>))}{!(q.data || []).length && <Empty msg="No grading scales yet — create one to enable Sprint 11 Gradebook." />}</div>
    </div>
  );
}

